<template>
  <div class="title-box">
    <span class="title">{{ props.moduleData.title }}</span>
  </div>
  <div class="custom-subList">
    <el-table
      class="custom-table"
      :data="tableData.filter(item => !item.delete)"
      border
      style="width: 100%"
      header-cell-class-name="custom-header"
    >
      <el-table-column
        min-width="200px"
        :label="item.title"
        v-for="(item, index) in dynamicColumns"
      >
        <template #header>
          <span class="required-icon">{{ item.fieldConfig.renderConfig.required ? '*' : '' }}</span>
          <span>{{ item.title }}</span>
        </template>
        <template #default="scope">
          <div class="val">
            <Render
              :sceneType="SceneTypeEnum.FLOW_DETAIL"
              :data="item"
              :comp-name="item.fieldConfig.renderConfig.compName"
              :renderConfig="item.fieldConfig.renderConfig"
              :quoteMappingConfig="quoteMappingConfig"
              :value="getValue(item.fieldConfig, scope.row)"
              @changeValue="value => changeValue(value, item.fieldConfig, scope.row)"
              @refreshDataList="value => refreshDataList(value, item.fieldConfig)"
              :isTxt="isText || scope.row.isTxt"
              :positions="positions"
              isSubTable
              :subFieldFormData="subFieldFormData"
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" min-width="180px">
        <template #default="scope">
          <div>
            <template v-if="!scope.row.isTxt">
              <!-- <RxkButton text @click="handleCancel(scope.row, scope.$index)">取消</RxkButton> -->
              <RxkButton text @click="handleSave(scope.row, scope.$index)">保存</RxkButton>
            </template>
            <RxkButton
              v-else-if="scope.row.isTxt && editData() && !readonly"
              text
              @click="handleEdit(scope.row)"
            >
              编辑
            </RxkButton>
            <RxkButton
              text
              v-if="deleteData() && !readonly && tableData.filter(item => !item.delete).length > 1"
              @click="handleDelete(scope.row, scope.$index)"
            >
              删除
            </RxkButton>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <span
      class="addRow pointer"
      v-if="(multipleData() && addData() && !readonly) || tableData.length < 1"
      @click="handleAddTableRow"
    >
      +新增一行
    </span>
  </div>
</template>
<script setup lang="tsx">
import { onMounted, type PropType, ref, unref } from 'vue'
import { ColumnType } from '@/types/table'
import { dealFormValConfig, getTableColumnWidthByCode } from '@/utils/preObjectManage/utils'
import Render from '@/components/custom/form/components/render.vue'
import { fieldTypeConfig, fieldTypeEnum } from '@/enums/preObjectManage'
import { formulaEmitter } from '@/utils/business/formulaEmitter'
import { crudSublist } from '@/apis/objectManage'
import { cloneDeep } from '@/utils/tools'
import { isArray, isNullOrUndefOrEmpty } from '@/utils/is'
import { SceneTypeEnum } from '@/enums/business'

const associaFormCondition = inject('associaFormCondition') as Recordable[]

const props = defineProps({
  moduleData: {
    type: Object as PropType<Recordable>,
    default: () => {}
  },
  data: {
    type: Object as PropType<Recordable>,
    required: true
  },
  positions: {
    type: Array as PropType<Recordable[]>,
    default: () => []
  },
  quoteMappingConfig: {
    type: Object as PropType<Recordable>,
    default: () => {}
  },
  subListValues: {
    type: Array as PropType<Recordable[]>,
    default: () => []
  },
  deleteSublistIds: {
    type: Array as PropType<Recordable[]>,
    default: () => []
  },
  readonly: {
    type: Boolean,
    default: false
  },
  subFieldFormData: {
    type: Object as PropType<Recordable>,
    default: () => ({})
  }
})

const emits = defineEmits(['save'])
const equationList = inject('equationList') as Recordable[]

const tableData = ref<Recordable[]>([])
const copyTableData = ref<Recordable[]>([])
const subListRows = ref<Recordable[]>([])
// 当前子表单中未保存的数据
const unSavaList = computed(() => tableData.value?.filter(item => !item.isTxt) || [])

// 根据当前子表字段id获取子表数据
async function getSubListData() {
  const subListIds: string[] = []
  const subListSerials: string[] = []
  props.moduleData.componentList.forEach((field: Recordable) => {
    const { id, fieldSerial } = field.columnInfo
    id && subListIds.push(id)
    subListSerials.push(fieldSerial)
  })

  await crudSublist({
    columns: subListIds,
    columnSerials: subListSerials,
    tableCode: props.data.tableCode,
    queryType: 2,
    type: 3,
    dataId: props.data.id,
    pageNum: 1,
    pageSize: 100,
    ignoreDataScope: true
  }).then(res => {
    tableData.value = res.records.map((item: Recordable) => {
      // 与流程暂存的sublist数据merge
      const subValue = props.subListValues.find(subValue => subValue.id === item.id)
      if (subValue) {
        for (let key in item) {
          item[key] = subValue[key] || item[key]
        }
      }
      return {
        ...item,
        isTxt: true
      }
    })

    // 将详情的数据合并
    tableData.value = props.subListValues
      ?.filter((item: Recordable) => item.moduleGroup === props.moduleData?.group)
      ?.concat(tableData.value)

    if (!res?.records?.length) {
      // TODO  在新增被驳回之后 子表单的数据是没有的 需要将流程返回的数据反显过来
      tableData.value =
        props.subListValues
          ?.filter((item: Recordable) => item.moduleGroup === props.moduleData?.group)
          .map(el => {
            let colMap =
              props?.moduleData?.componentList?.map((col: Recordable) => ({
                fieldSerial: col.fieldSerial,
                fieldCode: col.fieldCode
              })) || []
            colMap.forEach((col: Recordable) => {
              el[col.fieldCode] = el[col.fieldCode] || el[col.fieldSerial]
            })
            return {
              ...el,
              isTxt: true
            }
          }) || []
    }
    copyTableData.value = JSON.parse(JSON.stringify(tableData.value))
    // 默认保存一遍
    tableData.value.forEach((data, i) => {
      handleSave(data, i, true)
    })

    // 【配合后端特殊处理】数组对象去重后，过滤掉所有的被删除的子表数据
    tableData.value = [...new Map(tableData.value.map(item => [item.id, item])).values()].filter(
      item => !props.deleteSublistIds.includes(item.id)
    )

    // 必填且展示状态的字段未填写，则改为待保存状态(为了走到子表单必填校验逻辑)
    const _componentList =
      props?.moduleData?.componentList?.filter(
        (item: Recordable) => item.renderConfig.required && item.show
      ) || []
    tableData.value =
      tableData.value?.map(item => ({
        ...item,
        isTxt: _componentList.every((item2: Recordable) => item[item2.fieldCode])
      })) || []
  })
}

// 获取值
function getValue(data: Recordable, form: Recordable) {
  const { fieldCode, code } = data
  const getValueFn = dealFormValConfig[code]
    ? dealFormValConfig[code].getValue
    : () => form[fieldCode] || ''
  return getValueFn(data, form)
}

// 修改表单的值
function changeValue(value: any, data: Recordable, form: Recordable) {
  const { fieldCode, code, fieldType } = data
  let customCode = code
  // 引用字段
  if (
    fieldType === fieldTypeEnum.SYS_QUOTE ||
    fieldType === fieldTypeEnum.CUSTOM_QUOTE ||
    fieldType === fieldTypeEnum.SON_SYS_QUOTE ||
    fieldType === fieldTypeEnum.SON_CUSTOM_QUOTE
  ) {
    customCode = fieldTypeConfig.quote
  }
  // 映射字段
  if (
    fieldType === fieldTypeEnum.SYS_MAPPING ||
    fieldType === fieldTypeEnum.CUSTOM_MAPPING ||
    fieldType === fieldTypeEnum.SON_SYS_MAPPING ||
    fieldType === fieldTypeEnum.SON_CUSTOM_MAPPING
  ) {
    customCode = fieldTypeConfig.mapping
  }
  const setValueFn = dealFormValConfig[customCode]
    ? dealFormValConfig[customCode].setValue
    : () => {
        form[fieldCode] = value
      }
  setValueFn(value, data, form)
  // 只要是数字，金额，百分比就去触发
  if (
    code === fieldTypeConfig.number ||
    code === fieldTypeConfig.money ||
    code === fieldTypeConfig.percentage
  ) {
    formulaEmitter(unref(equationList), data, tableData, props?.moduleData?.componentList)
  }
  if (unref(associaFormCondition).length) {
    unref(associaFormCondition).forEach(item => {
      const cur = item.columnSerials.find(serial => serial === data.fieldSerial)
      if (cur) {
        item.Emitter.emit('associaForm', item.moduleSerial)
      }
    })
  }
}

function refreshDataList(value: any, item: Recordable) {
  item.dataList = value
  item.renderConfig.dataList = value
}

const dynamicColumns = ref<Recordable[]>([])

const defaultRowData: Recordable = ref({})
function handleDelete(data: Recordable, index: any) {
  if (tableData.value.length <= 1) {
    ElMessage.error('至少有一行')
    return
  }
  data.delete = true
  handleSave(data, index)
}

function handleEdit(data: Recordable) {
  data.isTxt = false
}
function handleCancel(data: Recordable, index: any) {
  // 取消需要还原数据
  if (data.id) {
    // 已有数据还原
    const copyData = copyTableData.value[index]
    tableData.value.splice(index, 1, copyData)
  } else {
    const cloneData = cloneDeep({ ...defaultRowData.value })
    tableData.value.splice(index, 1, { ...cloneData, isTxt: true })
  }
}
function handleSave(data: Recordable, i: number, isInit?: boolean) {
  let errorMessage = ''
  let columns = Object.keys(data)
    .filter(item => item !== 'isTxt')
    .map(sub => {
      const current = props.moduleData.componentList.find(
        (item: Recordable) => item.fieldCode === sub
      )
      const value = data[sub] ? (isArray(data[sub]) ? data[sub].join(',') : data[sub]) : ''
      if (current?.renderConfig?.required && isNullOrUndefOrEmpty(value) && !isInit) {
        errorMessage = errorMessage || `必填项${current.name}未填写不可审批通过`
      }
      return {
        fieldCode: current?.fieldCode,
        columnId: current?.id,
        columnSerial: current?.fieldSerial,
        value: value,
        oldValue: unref(copyTableData)?.[i]?.[current?.fieldCode] || ''
      }
    })
  if (errorMessage) {
    ElMessage.error(errorMessage)
    return
  }
  columns = columns.filter(item => item.columnId) // 过滤掉操作栏
  const row = {
    columns,
    delete:
      tableData.value?.find(item => item.id === data.id)?.delete ||
      props?.deleteSublistIds?.includes(data.id) ||
      false,
    rowDataId: data.id || 0
  }
  const index = subListRows.value.findIndex(itme => itme.rowDataId === data.id)
  if (index > -1) {
    subListRows.value.splice(index, 1, row)
  } else {
    subListRows.value.push(row)
  }
  emits('save', {
    defineType: props.moduleData.defineType,
    moduleGroup: props.moduleData.group,
    rows: unref(subListRows)
  })
  data.isTxt = true
}

// 使用策略模式设置表格的列
function setTableColumns() {
  const obj: Recordable = {}
  props.moduleData.componentList.forEach((field: Recordable) => {
    const { fieldCode } = field.columnInfo
    obj[fieldCode] = ''
  })
  // 生成默认的表格数据
  defaultRowData.value = obj
}
function handleAddTableRow() {
  const cloneData = cloneDeep({ ...defaultRowData.value })
  tableData.value.push({ ...cloneData, isTxt: false })
}
const isText = ref(true)
onMounted(async () => {
  await getSubListData()
  setTableColumns()
  createTableColumns()

  // 表格无值则默认添加一行
  if (!tableData.value?.filter(item => !item.delete)?.length) {
    handleAddTableRow()
  }

  setTimeout(() => {
    isText.value = false
  }, 1000)
})
function createTableColumns() {
  dynamicColumns.value = props.moduleData.componentList.map((field: Recordable) => {
    const { fieldCode, name, code } = field.columnInfo
    let resultObj: ColumnType = {
      key: fieldCode,
      title: name
    }
    // 设置每列的最小宽度,不手动设置的话则以通用规则设置
    if (!resultObj.minWidth && !resultObj.width) {
      resultObj.minWidth = getTableColumnWidthByCode(code)
    }
    resultObj.fieldConfig = JSON.parse(JSON.stringify(field))
    return { ...resultObj, ...field }
  })
}

// 是否允许添加多行数据
function multipleData() {
  console.log(props.moduleData, 'moduleData')
  if (props.moduleData.operateList) {
    const current = props.moduleData.operateList.find(
      item => item.code === 'MODULE:ALLOW_MULTIPLE_DATA'
    )
    return current ? current.value : false
  } else {
    return false
  }
}

// 是否可以新增记录
function addData() {
  console.log(props.moduleData, 'moduleData')
  if (props.moduleData.operateList) {
    const current = props.moduleData.operateList.find(item => item.code === 'MODULE:ADD_DATA')
    return current ? current.value : false
  } else {
    return false
  }
}
// 是否删除已有记录
function deleteData() {
  console.log(props.moduleData, 'moduleData')
  if (props.moduleData.operateList) {
    const current = props.moduleData.operateList.find(item => item.code === 'MODULE:DELETE_DATA')
    return current ? current.value : false
  } else {
    return false
  }
}
// 是否编辑已有记录
function editData() {
  console.log(props.moduleData, 'moduleData')
  if (props.moduleData.operateList) {
    const current = props.moduleData.operateList.find(item => item.code === 'MODULE:UPDATE_DATA')
    console.log(current, 'current123')
    return current ? current.value : false
  } else {
    return false
  }
}

defineExpose({
  unSavaList
})
</script>
<style lang="scss" scoped>
.title-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 38px;
  .title {
    font-size: 14px;
    color: #333333;
    font-weight: 700;
    display: inline-block;
    border-left: 3px solid #5687ff;
    padding-left: 9px;
  }
}
.custom-subList {
  width: 100%;
  height: 100%;
  &.reHeight {
    height: calc(100% - 65px);
  }
  .required-icon {
    color: #f56c6c;
  }

  :deep(.el-table__body) {
    .el-table__cell {
      padding: 0;
      height: 40px;
      line-height: 40px;
      border-right-color: transparent;
      .cell {
        line-height: 40px;
        font-size: 14px;
        color: #333333;
      }
    }
  }
  :deep(.custom-header) {
    padding: 0;
    height: 40px;
    line-height: 40px;
    background: #f4f4f5 !important;
    .cell {
      line-height: 40px;
      font-size: 14px;
      color: #666666;
      font-weight: 700;
    }
  }
}
.addRow {
  line-height: 48px;
  color: #5687ff;
  font-size: 14px;
}
</style>
